/**
 * 74.7 (DECISIONS 348): the night sky as part of the system — pure, deterministic, nothing in the Match.
 *
 * Today's dressing stars (rendering/renderer.ts `createStarfield`) are 2,290 points placed by `Math.random` about the world
 * origin: a different sky every load, no relation to the system in the sky (74.1), faded as one by the air. This gives a
 * system its own **star field** from its seed: a catalogue of directions in the system's frame with a magnitude (the
 * astronomer's: 6.5 the faintest a dark sky shows, −1.5 the brightest), a colour class, a galactic band (a great circle the
 * system's plane is tilted against, where nearly half the stars crowd and a faint dust of fainter ones makes the milky
 * glow), the dozen brightest as the sky's landmarks, and sometimes a **companion sun** (a wide binary: a brilliant point far
 * brighter than any star, its light not in the sim).
 *
 * **One sky brightness** for everything that shows at night: `skyBrightness` is the rule 74.1's `visibility` uses for the
 * bodies (0.1 × daylight × air over a night floor), and `limitingMagnitude` turns it into the faintest star seen — so the
 * stars fade by magnitude at dusk as the bodies fade by brightness, the brightest last, and an airless world shows them by
 * day. 74.1's night floor (0.002, a fiftieth of a day sky) is a city's sky: under it a far giant round a dim star is lost
 * from its own moon (74.6's finding) and only stars brighter than the third magnitude would show; `NIGHT_FLOOR.proposed`
 * (0.0002) is a dark country sky, the field full and the far giant back. The sheet (design_sheets/night_sky.html) shows both.
 *
 * **Meteors**: the shooting stars' rate from the system rather than the sim's planet count (rendering/shootingStars.ts
 * `skyCharacter`): a belt near the battle world's orbit, a giant's moon in its debris, a ring overhead; each belt a radiant
 * (a fixed direction in the system frame the streaks fan out from, as a real shower's), and the sporadics from anywhere.
 */
import { SeededRandom } from './random';
import { dsin, dcos, dlog, dpow } from './dmath';
import type { StarSystem, SystemBody } from './systemGen';
import type { StarType, Vec3 } from '../types';

export type StarClass = 'blue' | 'white' | 'yellow' | 'orange' | 'red';
export interface FieldStar {
  /** the direction, unit, in the system's frame (y the system plane's normal) */
  dir: Vec3;
  /** the apparent magnitude: smaller is brighter (−1.5 … 8.5; over 6.5 is the band's dust) */
  mag: number;
  cls: StarClass;
  /** in the galactic band */
  band: boolean;
}
export interface Companion { dir: Vec3; mag: number; type: StarType; separationAu: number }
export interface StarField {
  seed: string;
  stars: FieldStar[];
  /** the galactic plane: its normal in the system's frame, and the band's half-width as sin(latitude) */
  band: { normal: Vec3; halfWidth: number };
  /** the indices of the brightest `STAR_FIELD.named` stars: the sky's landmarks */
  brightest: number[];
  companion: Companion | null;
}

/** The numbers: how many stars, the magnitudes, the band's share and width, the dust, the named few, how often a companion. */
export const STAR_FIELD = {
  count: 3200, dust: 2600,
  magMin: -1.5, magMax: 6.5, dustMag: [5.8, 7.4] as const,   // (the dust within reach of a dark sky's limit, 6.5: a third of it shows)
  bandShare: 0.45, bandHalfWidth: 0.22,
  named: 12,
  companionChance: 0.25,
} as const;

/** The night floor of the sky's brightness: 74.1's (visibility's 0.002) and the proposed dark sky (0.0002). */
export const NIGHT_FLOOR = { today: 0.002, proposed: 0.0002 } as const;   // DECISIONS 352: the owner picked the dark sky ("I like B for night sky"); `proposed` is the game's
/** The sky's brightness at the viewer (a day sky under full air = 0.1; 74.1's visibility rule), over the night floor. */
export function skyBrightness(daylight: number, air: number, floor: number = NIGHT_FLOOR.today): number {
  return 0.1 * daylight * air + floor;
}
/** The faintest magnitude a sky of brightness `b` shows: 6.5 at the proposed floor (a dark night), ~3 at 74.1's floor, −3 under a full day sky. */
export function limitingMagnitude(b: number): number {
  return STAR_FIELD.magMax - 3.5 * dlog(Math.max(1e-9, b) / NIGHT_FLOOR.proposed) / Math.LN10;
}
/** How far a star of magnitude `mag` shows against the limit: 1 well inside, 0 past it, over a magnitude and a half. */
export function starShown(mag: number, limit: number): number {
  const t = Math.max(0, Math.min(1, (limit + 0.5 - mag) / 1.5));
  return t * t * (3 - 2 * t);
}

const CLASSES: Array<[StarClass, number]> = [['blue', 0.08], ['white', 0.25], ['yellow', 0.30], ['orange', 0.25], ['red', 0.12]];
/** A class by the roll, the bright stars leaning blue-white (the far-seen giants). */
function classOf(rng: SeededRandom, mag: number): StarClass {
  let u = rng.next();
  if (mag < 1.5) u *= 0.55;   // the bright ones: more often blue or white
  let acc = 0;
  for (const [c, w] of CLASSES) { acc += w; if (u < acc) return c; }
  return 'red';
}
/** A magnitude: the count brighter than m grows as 10^(0.5 m), so faint stars are many and bright ones few. */
function magnitudeOf(rng: SeededRandom): number {
  const u = Math.max(1e-6, rng.next());
  return Math.max(STAR_FIELD.magMin, STAR_FIELD.magMax + 2 * dlog(u) / Math.LN10);
}
function unitFrom(rng: SeededRandom): Vec3 {
  const z = 2 * rng.next() - 1, t = rng.next() * Math.PI * 2, r = Math.sqrt(Math.max(0, 1 - z * z));
  return { x: r * dcos(t), y: z, z: r * dsin(t) };
}
function cross(a: Vec3, b: Vec3): Vec3 { return { x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x }; }
function unit(v: Vec3): Vec3 { const l = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) || 1; return { x: v.x / l, y: v.y / l, z: v.z / l }; }

/** The star field of a system (or of a seed), the same every time. */
export function starFieldFor(system: StarSystem | string | number): StarField {
  const seed = typeof system === 'object' ? system.seed : String(system);
  const rng = new SeededRandom(`${seed}#stars`);
  // the galactic plane: tilted 20°–70° from the system's plane, turned by a seeded angle
  const tilt = (20 + rng.next() * 50) * Math.PI / 180, turn = rng.next() * Math.PI * 2;
  const normal: Vec3 = { x: dsin(tilt) * dcos(turn), y: dcos(tilt), z: dsin(tilt) * dsin(turn) };
  const e1 = unit(cross(normal, Math.abs(normal.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 })), e2 = cross(normal, e1);
  const inBand = (lat: number): Vec3 => {   // a direction in the band: along the great circle, a little off it
    const a = rng.next() * Math.PI * 2, cl = dcos(lat), sl = dsin(lat);
    return { x: (e1.x * dcos(a) + e2.x * dsin(a)) * cl + normal.x * sl, y: (e1.y * dcos(a) + e2.y * dsin(a)) * cl + normal.y * sl, z: (e1.z * dcos(a) + e2.z * dsin(a)) * cl + normal.z * sl };
  };
  const bandLat = (): number => (rng.next() + rng.next() - 1) * STAR_FIELD.bandHalfWidth * 2;   // a triangular spread about the plane (radians, small)
  const stars: FieldStar[] = [];
  for (let i = 0; i < STAR_FIELD.count; i++) {
    const band = rng.next() < STAR_FIELD.bandShare;
    const mag = magnitudeOf(rng);
    stars.push({ dir: band ? inBand(bandLat()) : unitFrom(rng), mag, cls: classOf(rng, mag), band });
  }
  for (let i = 0; i < STAR_FIELD.dust; i++) {
    const mag = STAR_FIELD.dustMag[0] + rng.next() * (STAR_FIELD.dustMag[1] - STAR_FIELD.dustMag[0]);
    stars.push({ dir: inBand(bandLat() * 1.3), mag, cls: rng.next() < 0.5 ? 'white' : 'yellow', band: true });
  }
  const brightest = stars.map((s, i) => i).filter(i => !stars[i].band || stars[i].mag <= STAR_FIELD.magMax).sort((a, b) => stars[a].mag - stars[b].mag).slice(0, STAR_FIELD.named);
  // a companion sun: a quarter of systems, a dimmer type, 300–3,000 AU off in a direction near the system's plane
  let companion: Companion | null = null;
  if (rng.next() < STAR_FIELD.companionChance) {
    const types: StarType[] = ['redDwarf', 'redDwarf', 'orangeDwarf', 'yellowDwarf'];
    const type = types[rng.int(0, types.length - 1)];
    const separationAu = 300 * dpow(10, rng.next());
    const lum = type === 'redDwarf' ? 0.02 : type === 'orangeDwarf' ? 0.3 : 1;
    // the Sun at 1 AU is −26.7; the companion's magnitude from its luminosity and distance (the square)
    const mag = -26.7 - 2.5 * dlog(lum / (separationAu * separationAu)) / Math.LN10;
    const a = rng.next() * Math.PI * 2, lat = (rng.next() - 0.5) * 0.4;
    companion = { dir: { x: dcos(a) * dcos(lat), y: dsin(lat), z: dsin(a) * dcos(lat) }, mag, type, separationAu };
  }
  return { seed, stars, band: { normal, halfWidth: STAR_FIELD.bandHalfWidth }, brightest, companion };
}

/** The meteors of a place: streaks a minute and why, and the radiants (directions in the system's frame) the showers fan out from. */
export interface MeteorCharacter { perMinute: number; why: string[]; radiants: Array<{ dir: Vec3; belt: number }> }
export const METEORS = { base: 1.2, belt: 2.5, giantMoon: 1.6, ring: 2.0, crowded: 1.3, sparse: 0.6 } as const;
export function meteorsFor(system: StarSystem, body: SystemBody): MeteorCharacter {
  const rng = new SeededRandom(`${system.seed}#meteors`);
  const parent = body.parent === null ? null : system.bodies[body.parent];
  const orbitAu = parent ? parent.orbitAu : body.orbitAu;
  let rate = METEORS.base;
  const why: string[] = [];
  const near = system.belts.filter(b => b > orbitAu * 0.5 && b < orbitAu * 2);
  if (near.length) { rate *= METEORS.belt; why.push(`a belt near the orbit (${near.map(b => b.toFixed(2)).join(', ')} AU)`); }
  if (parent && parent.kind !== 'rocky' && parent.kind !== 'icy') { rate *= METEORS.giantMoon; why.push(`a giant's moon, in its debris`); }
  if (parent?.ring) { rate *= METEORS.ring; why.push('a ring overhead'); }
  const n = system.bodies.length;
  if (n >= 10) { rate *= METEORS.crowded; why.push(`a crowded system (${n} bodies)`); }
  else if (n <= 4) { rate *= METEORS.sparse; why.push(`a sparse system (${n} bodies)`); }
  // a radiant per belt: a direction near the system's plane, the same every time
  const radiants = system.belts.map(belt => { const a = rng.next() * Math.PI * 2, lat = (rng.next() - 0.5) * 0.5; return { dir: { x: dcos(a) * dcos(lat), y: dsin(lat), z: dsin(a) * dcos(lat) }, belt }; });
  return { perMinute: rate, why, radiants };
}
